/**
 * ASSESSMENT RUNS — create a named grid and put systems in it.
 *
 * ── WHAT THIS SCREEN IS, AND WHY IT EXISTS ───────────────────────────────────────────
 *
 * The module's chain is
 *
 *     Business Units → Capability Model → [ THIS SCREEN ] → Assessment Grid → Scores
 *
 * and the middle step had no screen at all until 2026-10-08. The server had held
 * `POST /initiatives/:id/assessment-units` since Phase 2 with nothing calling it, so no
 * assessment unit could be created through the product — which meant the Assessment Grid, the
 * Scores and the whole scoring half could only ever render empty states. The owner found it
 * from the far end: *"Why I am not seeing anything on Assessment Grid screen"*, with every
 * picker correctly set against a database holding zero units.
 *
 * ── THE GRID IS A SAVED RECORD, NOT A SET OF FILTERS ─────────────────────────────────
 *
 * The owner's instruction, same day:
 *
 *   *"I need to have a system be able to pull down the systems and then create assessment grid
 *   and save the versions of these assessment grids based on specific initiative capability
 *   model functional area in a business unit … so I can have 15 different SAP system under one
 *   assessment grid"*
 *
 * So an assessment grid (`0107`) is a RUN: a scope, a name, and the systems in it. This screen
 * creates runs and fills them; the Assessment Grid screen opens one and records the percentages.
 *
 * NO VERSIONS — `0108`, the owner's correction: *"I do not need versions ... it is only one
 * version or entire run will be cloned."* A second run of a scope is a CLONE, which claims no
 * lineage: nothing points at the original and either can be renamed or deleted freely. Two runs
 * of one scope are told apart by their NAME, which is why a clone gets "(copy)".
 *
 * It is still why the scope could not stay as four loose URL parameters: two runs of the same
 * scope must be able to hold the same systems, which the old unique key on (initiative, business
 * unit, area, application) forbade outright.
 *
 * ── EVERY SYSTEM COMES FROM APR ──────────────────────────────────────────────────────
 *
 * Owner's rule, 2026-10-08: *"if the client doesn't use APR module, they should always host
 * their applications in the APR module … every system has to be defined in APR module."* So
 * there is no system-authoring screen in this module and this one only ever READS. The read
 * still goes through `GET /initiatives/:id/systems`, never a table name, because
 * `systemSource.ts` is what knows which source an initiative's operating mode implies — and
 * D-001 forbids this module touching the `apr` schema directly at all.
 *
 * ── THE PATTERN ──────────────────────────────────────────────────────────────────────
 *
 * D-018 — the Knowledge Library record-list (`../chrome/recordList.css`), as `BusinessUnitHierarchy`
 * clones it. D-017 — creating a run is a RIGHT-SIDE PANEL, never a window. D-014 — initiative,
 * model, area and business unit are records, so all four are `SelectField`; the system list is a
 * multi-select over hundreds of rows, which is a table with tick boxes rather than a picker.
 *
 * The header and the run list never scroll; the system table is the one scroller.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ConfirmDialog, SelectField } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { FormPanel } from '../chrome/FormPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { ERP_PROMPT } from '../prompts';
import { erpApi, ErpApiError } from '../api';
import { canEditCapabilityModel } from '../roles';
import {
  type AssessmentGridListRow,
  type AssessmentUnitRow,
  type BusinessUnit,
  type CapabilityModelVersionRow,
  type ConsolidationSystem,
  type ErpContext,
  type FunctionalAreaOption,
  type Initiative,
} from '../types';
import '../chrome/recordList.css';
import './SystemSelection.css';

const SystemSelectionHelp: Help = {
  title: 'Assessment Runs',
  icon: '🖥️',
  purpose:
    'Creates the <strong>assessment grids</strong> an initiative is scored from. A grid is one <strong>run</strong>: a business unit, a functional area of a capability model, and the systems that unit runs for it. <strong>Nothing can be assessed or scored until a run exists and has systems in it.</strong>',
  doHere: [
    'Pick an <strong>initiative</strong> to see its runs.',
    'Press <strong>New Assessment Grid</strong> to create one. Give it a <strong>name</strong>, and choose the capability model, functional area and business unit it covers.',
    'Select a run, then search the systems and <strong>tick</strong> the ones that business unit runs for that area. Press <strong>Add systems</strong>.',
    'Open a run in the <strong>Assessment Grid</strong> to record the overlap percentages.',
    '<strong>Clone</strong> a run to assess the same scope again &mdash; the systems come across, the recorded percentages do not.',
  ],
  fits: 'ERP Consolidation &rarr; Assessment Runs',
  firstTime:
    'Create one run for one business unit and one functional area, put the handful of systems that unit actually runs into it, then open it in the Assessment Grid. You do not have to cover the whole estate before you can score anything.',
  sections: [
    {
      type: 'defs',
      label: 'What a run is made of',
      items: [
        {
          term: 'Capability model',
          def: 'Which model&rsquo;s weights the scores are computed against. Recorded on the run, because an initiative can hold several models and they weight differently &mdash; a run that did not say which would silently re-column itself when a new model appeared.',
        },
        {
          term: 'Functional area',
          def: 'A <strong>top-level</strong> capability of that model. Its child capabilities become the columns of the grid.',
        },
        {
          term: 'Business unit',
          def: 'Who runs the systems. Defined once for the install under <em>Business Units</em>.',
        },
        {
          term: 'Systems',
          def: 'The applications that unit runs for that area. Always read from APR &mdash; every system is defined there, and nothing in this module authors one. A <strong>parent record</strong> is shown but cannot be selected: it groups the systems beneath it, so assessing it would double-count its children.',
        },
      ],
      note: 'The scope is fixed when the run is created. Changing which model or area a saved run covers would re-column it and invalidate every percentage already recorded, so a different scope is a different run.',
    },
    {
      type: 'html',
      label: 'Cloning, and what a copy carries',
      html: 'A clone takes the <strong>systems</strong> across and leaves the <strong>percentages blank</strong>. A copy that arrived looking already-assessed would be worse than an empty one, because an empty one is obviously empty. The original keeps everything it had, and the two are independent &mdash; renaming or deleting one does nothing to the other.',
    },
    {
      type: 'html',
      label: 'What cannot be undone casually',
      html: 'A run holding recorded assessments <strong>cannot be deleted</strong> &mdash; clone it instead and work in the copy. That refusal comes from the server, in its own words.',
    },
  ],
};

export function SystemSelection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setRoot, rootStyle } = useScreenLock();

  const initiativeId = searchParams.get('initiative') ?? '';
  /* Which run is open. A run carries its own scope, so this is the only scope parameter. */
  const gridId = searchParams.get('grid') ?? '';

  const [context, setContext] = useState<ErpContext | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [models, setModels] = useState<CapabilityModelVersionRow[]>([]);
  const [areas, setAreas] = useState<FunctionalAreaOption[]>([]);
  const [grids, setGrids] = useState<AssessmentGridListRow[]>([]);
  const [systems, setSystems] = useState<ConsolidationSystem[]>([]);
  const [systemTotal, setSystemTotal] = useState(0);
  const [units, setUnits] = useState<AssessmentUnitRow[]>([]);

  const [search, setSearch] = useState('');
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());

  const [newGrid, setNewGrid] = useState<{
    name: string;
    description: string;
    capabilityModelId: string;
    areaId: string;
    businessUnitId: string;
  } | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [gate, setGate] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDeleteGrid, setConfirmDeleteGrid] = useState<AssessmentGridListRow | null>(null);
  const [confirmRemoveUnit, setConfirmRemoveUnit] = useState<AssessmentUnitRow | null>(null);

  /* 1 — the user, the initiatives and the install's business units. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [contextPayload, initiativePayload, buPayload] = await Promise.all([
          erpApi.context(),
          erpApi.initiatives.list({ limit: 200 }),
          erpApi.businessUnits.list(),
        ]);
        if (cancelled) return;
        setContext(contextPayload.context);
        setInitiatives(initiativePayload.initiatives);
        setBusinessUnits(buPayload.business_units);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* 2 — the initiative's runs, its models, and the areas a run can cover. */
  const loadForInitiative = useCallback(async () => {
    if (!initiativeId) {
      setGrids([]);
      setModels([]);
      setAreas([]);
      setGate(null);
      return;
    }
    try {
      const [gridPayload, modelPayload] = await Promise.all([
        erpApi.assessmentGrids.list(initiativeId),
        erpApi.capabilityModels.listForInitiative(initiativeId),
      ]);
      setGrids(gridPayload.assessment_grids);
      setModels(modelPayload.capability_models);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load this initiative');
      return;
    }
    /*
     * The areas come from the SAME endpoint the Assessment Grid uses, so the areas a run can
     * cover are exactly the areas it can later be assessed in. It carries the same gate: a model
     * that is empty or does not total 100.0% refuses, in the server's own words.
     */
    try {
      const payload = await erpApi.assessmentGrid.areas(initiativeId);
      setAreas(payload.functional_areas);
      setGate(null);
    } catch (err) {
      setAreas([]);
      if (err instanceof ErpApiError && err.code === 'capability_model_not_baselined') {
        setGate(err.message);
        return;
      }
      setLoadError(err instanceof Error ? err.message : 'Failed to load the capability model');
    }
  }, [initiativeId]);

  useEffect(() => {
    void loadForInitiative();
  }, [loadForInitiative]);

  const grid = useMemo(() => grids.find((row) => row.id === gridId) ?? null, [grids, gridId]);

  /* 3 — the systems available, through the initiative's operating mode. */
  const loadSystems = useCallback(async () => {
    if (!initiativeId || !gridId) {
      setSystems([]);
      setSystemTotal(0);
      return;
    }
    setSystemsLoading(true);
    try {
      const payload = await erpApi.systems.listForInitiative(initiativeId, {
        ...(search.trim() === '' ? {} : { q: search.trim() }),
        limit: 500,
      });
      setSystems(payload.systems);
      setSystemTotal(payload.total ?? payload.systems.length);
    } catch (err) {
      setSystems([]);
      setLoadError(err instanceof Error ? err.message : 'Failed to load systems');
    } finally {
      setSystemsLoading(false);
    }
  }, [initiativeId, gridId, search]);

  /* Debounced: the search goes to the server, and 579 systems is more than a client filter
     should hold or a keystroke should re-fetch. */
  useEffect(() => {
    const t = window.setTimeout(() => void loadSystems(), 250);
    return () => window.clearTimeout(t);
  }, [loadSystems]);

  /* 4 — what is already in the open run. */
  const loadUnits = useCallback(async () => {
    if (!initiativeId || !grid) {
      setUnits([]);
      return;
    }
    try {
      const payload = await erpApi.assessmentUnits.list(initiativeId, {
        businessUnitId: grid.business_unit_id,
        functionalAreaCapabilityId: grid.functional_area_capability_id,
        limit: 500,
      });
      /* Filtered to THIS run: the endpoint answers by SCOPE, and a scope can have several runs
         once one has been cloned, so without this the clone's systems would appear in the
         original and vice versa. */
      setUnits(
        payload.assessment_units.filter((unit) => unit.assessment_grid_id === grid.id),
      );
    } catch {
      setUnits([]);
    }
  }, [initiativeId, grid]);

  useEffect(() => {
    void loadUnits();
  }, [loadUnits]);

  const canEdit = canEditCapabilityModel(context);
  const initiative = initiatives.find((row) => row.id === initiativeId) ?? null;
  const frozen = initiative?.baselined_at != null;

  const alreadyIn = useMemo(() => new Set(units.map((u) => u.application_id)), [units]);
  /*
   * A PARENT IS NOT TICKABLE. Owner, 2026-10-08: *"I should not be able to select the parent
   * application from APR - I should be able to see them but not select them."* It stays in the
   * list — you need to see that the group exists, and its children sit around it — but it
   * carries no tick box, and the server refuses one anyway (`parent_system_not_assessable`).
   */
  const tickable = useMemo(
    () => systems.filter((system) => !alreadyIn.has(system.id) && !system.is_parent),
    [systems, alreadyIn],
  );

  const openInitiative = (value: string) => {
    setTicked(new Set());
    setNotice(null);
    setActionError(null);
    setSearchParams(value ? { initiative: value } : {});
  };

  const openGrid = (value: string) => {
    setTicked(new Set());
    setNotice(null);
    setActionError(null);
    setSearch('');
    setSearchParams(value ? { initiative: initiativeId, grid: value } : { initiative: initiativeId });
  };

  const toggle = (systemId: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(systemId)) next.delete(systemId);
      else next.add(systemId);
      return next;
    });

  const tickAllShown = () =>
    setTicked((prev) => {
      const next = new Set(prev);
      const all = tickable.every((system) => next.has(system.id));
      for (const system of tickable) {
        if (all) next.delete(system.id);
        else next.add(system.id);
      }
      return next;
    });

  const createGrid = async () => {
    if (!newGrid) return;
    setPanelError(null);
    if (!newGrid.name.trim()) return setPanelError('Name is required.');
    if (!newGrid.capabilityModelId) return setPanelError('Choose the capability model.');
    if (!newGrid.areaId) return setPanelError('Choose the functional area.');
    if (!newGrid.businessUnitId) return setPanelError('Choose the business unit.');
    setBusy(true);
    try {
      const payload = await erpApi.assessmentGrids.create(initiativeId, {
        name: newGrid.name.trim(),
        description: newGrid.description.trim() || null,
        capability_model_id: newGrid.capabilityModelId,
        functional_area_capability_id: newGrid.areaId,
        business_unit_id: newGrid.businessUnitId,
      });
      setNewGrid(null);
      await loadForInitiative();
      openGrid(payload.assessment_grid.id);
      setNotice('Run created. Tick the systems this business unit runs for that area.');
    } catch (err) {
      setPanelError(err instanceof Error ? err.message : 'Could not create that run.');
    } finally {
      setBusy(false);
    }
  };

  const addSystems = async () => {
    if (!grid || ticked.size === 0) return;
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      const payload = await erpApi.assessmentUnits.create(
        initiativeId,
        grid.id,
        [...ticked].map((applicationId) => ({ application_id: applicationId })),
      );
      setTicked(new Set());
      await Promise.all([loadUnits(), loadForInitiative()]);
      setNotice(
        `${payload.created} system${payload.created === 1 ? '' : 's'} added. ` +
          'They are the rows of this grid in the Assessment Grid screen.',
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not add those systems');
    } finally {
      setBusy(false);
    }
  };

  const deleteGrid = async () => {
    if (!confirmDeleteGrid) return;
    setBusy(true);
    setActionError(null);
    try {
      await erpApi.assessmentGrids.remove(confirmDeleteGrid.id);
      const removed = confirmDeleteGrid.id;
      setConfirmDeleteGrid(null);
      await loadForInitiative();
      if (gridId === removed) openGrid('');
      setNotice('Run deleted.');
    } catch (err) {
      setConfirmDeleteGrid(null);
      setActionError(err instanceof Error ? err.message : 'Could not delete that run');
    } finally {
      setBusy(false);
    }
  };

  const removeUnit = async () => {
    if (!confirmRemoveUnit) return;
    setBusy(true);
    setActionError(null);
    try {
      await erpApi.assessmentUnits.remove(initiativeId, confirmRemoveUnit.id);
      setConfirmRemoveUnit(null);
      await Promise.all([loadUnits(), loadForInitiative()]);
      setNotice('Taken out of this run.');
    } catch (err) {
      setConfirmRemoveUnit(null);
      setActionError(err instanceof Error ? err.message : 'Could not remove that system');
    } finally {
      setBusy(false);
    }
  };

  const newGridButton = (
    <button
      type="button"
      className="erp-rl-btn erp-rl-btn--primary"
      disabled={!initiativeId || frozen || areas.length === 0}
      onClick={() => {
        setPanelError(null);
        setNewGrid({
          name: '',
          description: '',
          /* Seeded with the newest model and the first area — the common case, and both are
             editable. A panel that opens empty asks four questions before it asks the one that
             matters, which is what the run is called. */
          capabilityModelId: models[0]?.id ?? '',
          areaId: areas[0]?.capability_id ?? '',
          businessUnitId: businessUnits[0]?.id ?? '',
        });
      }}
    >
      <i className="material-icons">add</i>
      New Assessment Grid
    </button>
  );

  return (
    <ErpScreen>
      <div className="page ss-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={SystemSelectionHelp} />
        <PageHeader
          title="Assessment Runs"
          subtitle="A named grid per business unit and functional area — and the systems it assesses"
          actions={canEdit ? newGridButton : null}
        />

        <div className="erp-rl-filter-bar ss-scope">
          <SelectField
            label="Initiative"
            width="lg"
            id="erp-ss-initiative"
            value={initiativeId}
            placeholder={ERP_PROMPT.initiative}
            options={initiatives.map((row) => ({
              value: row.id,
              id: row.name,
              description: row.description ?? undefined,
            }))}
            onChange={openInitiative}
          />
          <SelectField
            label="Assessment grid"
            width="lg"
            id="erp-ss-grid"
            disabled={!initiativeId || grids.length === 0}
            value={gridId}
            placeholder={grids.length === 0 ? '— none yet —' : '— Select a Run —'}
            allowClear={false}
            options={grids.map((row) => ({
              value: row.id,
              id: row.name,
              description:
                `${row.business_unit_name ?? '?'} · ${row.functional_area_name ?? '?'} · ` +
                `${row.system_count} system${row.system_count === 1 ? '' : 's'}` +
                `${row.assessed_count > 0 ? ` · ${row.assessed_count} recorded` : ''}`,
            }))}
            onChange={openGrid}
          />
        </div>

        {gate !== null && (
          <div className="erp-panel-error" style={{ flexShrink: 0 }}>
            <i className="material-icons erp-rl-error-icon">error</i>
            {gate}
          </div>
        )}
        {actionError !== null && (
          <div className="erp-panel-error" style={{ flexShrink: 0 }}>
            <i className="material-icons erp-rl-error-icon">error</i>
            {actionError}
          </div>
        )}
        {frozen && (
          <div className="ss-frozen">
            This initiative is baselined, so its runs and their systems are fixed. A baseline
            captures a snapshot of exactly this set, which is what makes a published result
            reproducible.
          </div>
        )}
        {notice !== null && (
          <div className="ss-results">
            <span>{notice}</span>
            <button
              type="button"
              className="erp-rl-btn erp-rl-btn--ghost"
              onClick={() => setNotice(null)}
            >
              <i className="material-icons">clear</i>
              Dismiss
            </button>
          </div>
        )}

        {/* The open run: what it covers, and what you can do with it. */}
        {grid && (
          <div className="ss-run">
            <div className="ss-run__text">
              <span className="ss-run__name">{grid.name}</span>
              <span className="ss-run__scope">
                {grid.business_unit_name} · {grid.functional_area_name} ·{' '}
                {grid.capability_model_name} v{grid.capability_model_version} ·{' '}
                {grid.system_count} system{grid.system_count === 1 ? '' : 's'}
              </span>
            </div>
            <div className="ss-run__actions">
              <button
                type="button"
                className="erp-rl-btn erp-rl-btn--ghost"
                disabled={grid.system_count === 0}
                title={
                  grid.system_count === 0
                    ? 'Add systems first — a grid with no systems has no rows to assess'
                    : undefined
                }
                onClick={() =>
                  navigate(
                    `/erp_consolidation/assessment-grid?initiative=${encodeURIComponent(initiativeId)}` +
                      `&grid=${encodeURIComponent(grid.id)}`,
                  )
                }
              >
                <i className="material-icons">grid_on</i>
                Open Assessment Grid
              </button>
              {/* NO Clone run and NO Delete run — owner, 2026-10-08: *"I do not need 'clone
                  Run' nor Delete Run buttons"*. The endpoints stay (the server still answers
                  both, and the audit trail still records them); only the controls are gone. */}
            </div>
          </div>
        )}

        {/* The system search and the add action. */}
        {grid && gate === null && (
          <div className="erp-rl-filter-bar ss-actions">
            <div className="erp-rl-search">
              <i className="material-icons">search</i>
              <input
                type="text"
                className="erp-rl-input"
                placeholder="Search systems by name or ID…"
                aria-label="Search systems"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span className="ss-count">
              {systemsLoading
                ? 'Searching…'
                : `${systems.length} of ${systemTotal} system${systemTotal === 1 ? '' : 's'}` +
                  `${units.length === 0 ? '' : ` · ${units.length} in this run`}`}
            </span>
            <div className="ss-actions-end">
              {canEdit && !frozen && tickable.length > 0 && (
                <button type="button" className="erp-rl-btn erp-rl-btn--ghost" onClick={tickAllShown}>
                  <i className="material-icons">done_all</i>
                  {tickable.every((s) => ticked.has(s.id)) ? 'Clear shown' : 'Select shown'}
                </button>
              )}
              {canEdit && !frozen && (
                <button
                  type="button"
                  className="erp-rl-btn erp-rl-btn--primary"
                  disabled={busy || ticked.size === 0}
                  onClick={() => void addSystems()}
                >
                  <i className="material-icons">add</i>
                  {busy ? 'Adding…' : `Add systems${ticked.size === 0 ? '' : ` (${ticked.size})`}`}
                </button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="erp-rl-loading">
            <i className="material-icons erp-rl-spin">sync</i>
            <p>Loading…</p>
          </div>
        ) : loadError !== null ? (
          <div className="erp-panel-error">
            <i className="material-icons erp-rl-error-icon">error</i>
            {loadError}
          </div>
        ) : !initiativeId ? (
          <div className="erp-rl-empty">
            <div className="erp-rl-empty-icon">
              <i className="material-icons">dns</i>
            </div>
            <h2 className="erp-rl-empty-title">Choose an initiative</h2>
            <p className="erp-rl-empty-body">Its assessment runs will be listed here.</p>
          </div>
        ) : !grid ? (
          <div className="erp-rl-empty">
            <div className="erp-rl-empty-icon">
              <i className="material-icons">dns</i>
            </div>
            <h2 className="erp-rl-empty-title">
              {grids.length === 0 ? 'No assessment runs yet' : 'Select a run'}
            </h2>
            <p className="erp-rl-empty-body">
              {areas.length === 0
                ? 'This initiative’s capability model has no functional areas yet. Add at least one before creating a run.'
                : grids.length === 0
                  ? 'A run is one business unit’s systems, assessed for one functional area. Create the first one.'
                  : 'Pick a run above to see and change the systems in it.'}
            </p>
            {canEdit && grids.length === 0 && areas.length > 0 && (
              <div style={{ marginTop: 12 }}>{newGridButton}</div>
            )}
          </div>
        ) : systems.length === 0 && !systemsLoading ? (
          <div className="erp-rl-empty">
            <div className="erp-rl-empty-icon">
              <i className="material-icons">dns</i>
            </div>
            <h2 className="erp-rl-empty-title">
              {search.trim() === '' ? 'No systems available' : 'No matching systems'}
            </h2>
            <p className="erp-rl-empty-body">
              {search.trim() === ''
                ? 'Every system is defined in APR. If the list is empty, there are no applications to select.'
                : 'Try a different search.'}
            </p>
          </div>
        ) : (
          <div className="erp-rl-table-wrapper">
            <table className="erp-rl-table ss-table">
              <thead>
                <tr>
                  <th className="ss-tick-col" />
                  <th>System</th>
                  <th>Vendor</th>
                  <th>Type</th>
                  <th>Criticality</th>
                  <th className="erp-rl-right">Users</th>
                  <th>In this run</th>
                </tr>
              </thead>
              <tbody>
                {systems.map((system) => {
                  const inRun = alreadyIn.has(system.id);
                  const parent = system.is_parent;
                  const unit = inRun
                    ? (units.find((row) => row.application_id === system.id) ?? null)
                    : null;
                  return (
                    <tr
                      key={system.id}
                      className={`${inRun ? 'ss-row--in-scope' : ''}${parent ? ' ss-row--parent' : ''}`}
                    >
                      <td className="ss-tick-col">
                        {/* A system already in the run is not tickable: the server refuses a
                            duplicate by name, so the tick would be a control that fails. */}
                        {canEdit && !frozen && !inRun && !system.is_parent && (
                          <input
                            type="checkbox"
                            className="ss-tick"
                            checked={ticked.has(system.id)}
                            onChange={() => toggle(system.id)}
                            aria-label={`Select ${system.name}`}
                          />
                        )}
                      </td>
                      <td>
                        <span className="erp-rl-name">{system.name}</span>
                        {/* The child pill Application Master Data draws beside a child's name
                            (`ApplicationMasterDataTab.tsx:551`), with the parent's name added —
                            owner, 2026-10-09: "On a children app I need to see the parent info." */}
                        {system.parent_code !== null && (
                          <span className="ss-child-pill" title={system.parent_name ?? undefined}>
                            Child of {system.parent_code}
                            {system.parent_name ? ` · ${system.parent_name}` : ''}
                          </span>
                        )}
                        <span className="erp-rl-desc">
                          {system.application_id_code ?? '—'}
                          {system.l1_business_functional_area
                            ? ` · ${system.l1_business_functional_area}`
                            : ''}
                        </span>
                      </td>
                      <td className="erp-rl-sec">{system.vendor_supplier ?? 'N/A'}</td>
                      <td className="erp-rl-sec">{system.application_type ?? 'N/A'}</td>
                      <td>
                        {system.business_criticality ? (
                          <span className="erp-rl-badge erp-rl-badge--info">
                            {system.business_criticality}
                          </span>
                        ) : (
                          <span className="erp-rl-none">N/A</span>
                        )}
                      </td>
                      <td className="erp-rl-num">{system.user_count ?? '—'}</td>
                      <td>
                        {parent ? (
                          /* Said on the row, in this module's standing way of handling a control
                             the server would refuse: the reason replaces the control. */
                          <span
                            className="erp-rl-sec"
                            title="A parent groups the systems beneath it. Assessing it would double-count its children — select the children instead."
                          >
                            parent record
                          </span>
                        ) : inRun ? (
                          <div className="erp-rl-actions">
                            <span className="erp-rl-badge erp-rl-badge--success">In run</span>
                            {canEdit && !frozen && unit && (
                              <button
                                type="button"
                                className="erp-rl-glyph erp-rl-glyph--delete"
                                title="Take this system out of this run"
                                aria-label={`Remove ${system.name}`}
                                disabled={busy}
                                onClick={() => setConfirmRemoveUnit(unit)}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="erp-rl-none">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── A NEW RUN. A right-side panel at the single width (D-017). ────────────────── */}
        {newGrid && (
          <FormPanel
            open
            title="New Assessment Grid"
            subtitle={initiative?.name}
            onRequestClose={() => {
              setNewGrid(null);
              setPanelError(null);
            }}
            onSave={() => void createGrid()}
            busy={busy}
            saveLabel="Create Run"
            busyLabel="Creating…"
            dirty={newGrid.name.trim() !== '' || newGrid.description.trim() !== ''}
            discardMessage="Discard this run?"
          >
            {panelError && (
              <p className="fb-panel-error" role="alert">
                {panelError}
              </p>
            )}
            <div className="form-group">
              <label htmlFor="erp-ss-name">Name *</label>
              <input
                id="erp-ss-name"
                type="text"
                value={newGrid.name}
                onChange={(e) =>
                  setNewGrid((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                placeholder="e.g., FY27 Logistics — Automotive Components"
                required
              />
              <p className="ctl-hint">
                What this run is called. Versions of it keep the name and change the number.
              </p>
            </div>
            <div className="form-group">
              <label htmlFor="erp-ss-desc">Description</label>
              <textarea
                id="erp-ss-desc"
                rows={2}
                value={newGrid.description}
                onChange={(e) =>
                  setNewGrid((prev) => (prev ? { ...prev, description: e.target.value } : prev))
                }
                placeholder="What this run covers, and why"
              />
            </div>
            <div className="form-group">
              <SelectField
                label="Capability model"
                id="erp-ss-new-model"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                allowClear={false}
                value={newGrid.capabilityModelId}
                options={models.map((row) => ({
                  value: row.id,
                  id: row.name,
                  description:
                    `${row.model_key ? `${row.model_key} · ` : ''}v${row.version} · ` +
                    `${row.capability_count} capabilit${row.capability_count === 1 ? 'y' : 'ies'}` +
                    `${row.weights_balanced ? '' : ' · weights do not total 100.0%'}`,
                  disabled: !row.weights_balanced,
                }))}
                onChange={(value) =>
                  setNewGrid((prev) => (prev ? { ...prev, capabilityModelId: value } : prev))
                }
              />
              <p className="ctl-hint">
                Recorded on the run, because the scores are computed against{' '}
                <strong>this model&rsquo;s weights</strong>. A model whose levels do not total
                100.0% cannot be assessed against and is not offered.
              </p>
            </div>
            <div className="form-group">
              <SelectField
                label="Functional area"
                id="erp-ss-new-area"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                allowClear={false}
                value={newGrid.areaId}
                options={areas.map((row) => ({
                  value: row.capability_id,
                  id: row.code ?? row.name,
                  description: `${row.name} · ${row.capability_count} capabilit${row.capability_count === 1 ? 'y' : 'ies'}`,
                }))}
                onChange={(value) =>
                  setNewGrid((prev) => (prev ? { ...prev, areaId: value } : prev))
                }
              />
              <p className="ctl-hint">
                Its capabilities become the <strong>columns</strong> of the grid.
              </p>
            </div>
            <div className="form-group">
              <SelectField
                label="Business unit"
                id="erp-ss-new-bu"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                allowClear={false}
                value={newGrid.businessUnitId}
                options={businessUnits.map((row) => ({
                  value: row.id,
                  id: row.name,
                  description: row.region ?? undefined,
                }))}
                onChange={(value) =>
                  setNewGrid((prev) => (prev ? { ...prev, businessUnitId: value } : prev))
                }
              />
            </div>
            <p className="ctl-hint">
              The scope is <strong>fixed when the run is created</strong>. Changing which model or
              area a saved run covers would re-column it and invalidate every percentage already
              recorded, so a different scope is a different run.
            </p>
          </FormPanel>
        )}

        {confirmDeleteGrid && (
          <ConfirmDialog
            open
            title="Delete this run?"
            message={
              `${confirmDeleteGrid.name} and its ` +
              `${confirmDeleteGrid.system_count} system${confirmDeleteGrid.system_count === 1 ? '' : 's'} ` +
              'will be removed. A run holding recorded assessments cannot be deleted — clone it ' +
              'instead — and the server will say so.'
            }
            confirmLabel="Delete"
            onConfirm={() => void deleteGrid()}
            onCancel={() => setConfirmDeleteGrid(null)}
          />
        )}

        {confirmRemoveUnit && (
          <ConfirmDialog
            open
            title="Take this system out of the run?"
            message={
              `${confirmRemoveUnit.application_name ?? confirmRemoveUnit.application_id} will no ` +
              `longer be assessed in ${grid?.name ?? 'this run'}. A system that has already been ` +
              'assessed cannot be removed.'
            }
            confirmLabel="Remove"
            onConfirm={() => void removeUnit()}
            onCancel={() => setConfirmRemoveUnit(null)}
          />
        )}
      </div>
    </ErpScreen>
  );
}
